/**
 * J11: phone one-thumb layout (docs/design/2026-10-11-auto-play.md, ui/2026-10-11-bout-hud-phone.md).
 * Active when the viewport is narrow and touch-capable (body.phone-ui). Right thumb cluster: attack, guard, dash,
 * auto (the real #autoBtn moved in, with the budget ring), ต่อไป. Bottom bar: วันนี้, แผนที่, กระเป๋า, เมนู.
 * Hidden in a duel (body.duel-on): the duel HUD (duelHudCompact.ts) has its own touch controls.
 * No move pad exists in the client (touch moves by tap / drag in input.ts), so none is added here.
 */
import { autoQuestOf, type PlayerCommand, type World } from '@nine/shared';
import { h } from './dom';
import { attachAutoRing } from './autoRing';
import './phoneLayout.css';

export interface PhoneDeps {
  world: World;
  send: (c: PlayerCommand) => void;
  setBlock: (on: boolean) => void;
  dash: () => void;
  attack: () => void;
  openToday: () => void;
  openMap: () => void;
  openBag: () => void;
  toggleMenu: () => void;
}

const QUERY = '(max-width: 900px) and (pointer: coarse)';

export function installPhoneLayout(d: PhoneDeps): void {
  const mq = window.matchMedia(QUERY);
  const press = (el: HTMLElement, run: () => void) =>
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      run();
    });
  const btn = (cls: string, text: string, title: string) =>
    h('button', { type: 'button', class: `ph-btn ${cls}`, title, tabIndex: -1 }, text) as HTMLButtonElement;

  const atk = btn('ph-atk', 'โจมตี', 'โจมตี');
  press(atk, d.attack);
  const guard = btn('ph-guard', 'ป้องกัน', 'ป้องกัน (กดค้าง)');
  guard.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    d.setBlock(true);
  });
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave'] as const) guard.addEventListener(ev, () => d.setBlock(false));
  const dash = btn('ph-dash', 'พุ่ง', 'พุ่ง');
  press(dash, d.dash);
  const autoHost = h('div', { class: 'ph-auto' });
  const next = btn('ph-next', 'ต่อไป', 'เดินตามภารกิจ');
  press(next, () => d.send({ t: 'autoQuest', on: !autoQuestOf(d.world)?.on }));
  const cluster = h('div', { class: 'ph-cluster' }, atk, guard, dash, autoHost, next);

  const bar = h('div', { class: 'ph-bar' });
  for (const [t, run] of [
    ['วันนี้', d.openToday],
    ['แผนที่', d.openMap],
    ['กระเป๋า', d.openBag],
    ['เมนู', d.toggleMenu],
  ] as const) {
    const b = h('button', { type: 'button', tabIndex: -1 }, t) as HTMLButtonElement;
    press(b, run);
    bar.append(b);
  }
  const root = h('div', { id: 'phoneUi' }, cluster, bar);
  document.body.append(root);

  const auto = document.getElementById('autoBtn');
  const home = auto ? { parent: auto.parentElement!, next: auto.nextSibling } : null;
  const syncRing = attachAutoRing(autoHost, d.world);
  let shown = false;
  const apply = () => {
    const on = mq.matches;
    document.body.classList.toggle('phone-ui', on);
    if (auto && home) {
      if (on) autoHost.prepend(auto);
      else home.parent.insertBefore(auto, home.next);
    }
    if (on && !shown) d.openToday(); // the home screen
    shown = on;
  };
  mq.addEventListener('change', apply);
  apply();
  setInterval(() => {
    if (!shown) return;
    syncRing();
    const aq = !!autoQuestOf(d.world)?.on;
    next.classList.toggle('on', aq);
    next.textContent = aq ? 'หยุด' : 'ต่อไป';
  }, 500);
}
